import { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import { Stack } from '@mui/system';
import {
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

function not(a: readonly TransferListItem[], b: readonly TransferListItem[]) {
  return a.filter(
    ({ value }) => b.findIndex(({ value: bValue }) => value === bValue) === -1,
  );
}

function intersection(
  a: readonly TransferListItem[],
  b: readonly TransferListItem[],
) {
  return a.filter(
    ({ value }) => b.findIndex(({ value: bValue }) => value === bValue) !== -1,
  );
}

function union(a: readonly TransferListItem[], b: readonly TransferListItem[]) {
  return [...a, ...not(b, a)];
}

export interface TransferListItem {
  label: string;
  value: string;
}

interface TransferListProps {
  items: TransferListItem[];
  selectedItems: TransferListItem[];
  onSave?: (resultItems: readonly TransferListItem[]) => void;
  onCancel?: () => void;
}

export default function TransferList({
  items,
  selectedItems,
  onSave,
  onCancel,
}: TransferListProps) {
  const [checked, setChecked] = useState<readonly TransferListItem[]>([]);
  const [left, setLeft] = useState<readonly TransferListItem[]>(
    not(items, selectedItems),
  );
  const [right, setRight] =
    useState<readonly TransferListItem[]>(selectedItems);
  const [searchText, setSearchText] = useState<string>('');
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const filtered = useMemo(() => {
    return !searchText || !left || !left.length
      ? []
      : left.filter((e) =>
          e.label.toLowerCase().includes(searchText.toLowerCase()),
        );
  }, [left, searchText]);

  const handleToggle = (item: TransferListItem) => () => {
    const currentIndex = checked.findIndex(({ value }) => item.value === value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items: readonly TransferListItem[]) =>
    intersection(checked, items).length;

  const handleToggleAll = (items: readonly TransferListItem[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleSearchTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const str = event.currentTarget.value;
    setSearchText(str);
  };

  const handleSearchTextClear = () => {
    setSearchText('');
  };

  const customList = (items: readonly TransferListItem[]) => (
    <Card>
      <List
        sx={{
          bgcolor: 'background.paper',
          height: '380px',
          overflow: 'auto',
        }}
        dense
        component="div"
        role="list"
      >
        {items.map((item: TransferListItem) => {
          const { label, value } = item;
          const labelId = `transfer-list-all-item-${value}-label`;
          const itemIsChecked =
            checked.findIndex(
              ({ value: checkedValue }) => checkedValue === value,
            ) !== -1;
          return (
            <ListItem
              key={value}
              role="listitem"
              dense
              onClick={handleToggle(item)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={itemIsChecked}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={label}
                sx={{
                  color: itemIsChecked
                    ? (theme) => theme.palette.primary.main
                    : 'inherit',
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  const selectButton = (items: readonly TransferListItem[], side: string) => {
    return (
      <Button
        onClick={handleToggleAll(items)}
        variant="contained"
        color="primary"
        disabled={items.length === 0}
        disableElevation
        data-testid={`select-all-${side}`}
      >
        {items.length !== numberOfChecked(items) || items.length === 0
          ? 'Select All'
          : 'Unselect All'}
      </Button>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        size={{
          xs: 12,
          sm: 9,
        }}
      >
        <Grid size={12}>
          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel htmlFor="outlined-adornment-amount">Search</InputLabel>
            <OutlinedInput
              fullWidth
              size="small"
              label="Search"
              value={searchText}
              onChange={handleSearchTextChange}
              color="primary"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <CloseIcon
                    onClick={handleSearchTextClear}
                    sx={{ cursor: 'pointer' }}
                  />
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 5,
          }}
        >
          <Stack spacing={2} direction="column">
            <Typography
              component="h2"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              {`Available (${left.length})${
                !searchText ? '' : ` / Filtering (${filtered.length})`
              }`}
            </Typography>
            {customList(!searchText ? left : filtered)}
            {selectButton(!searchText ? left : filtered, 'available')}
          </Stack>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 2,
          }}
        >
          <Grid container direction="column" alignItems="center">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 5,
          }}
        >
          <Stack spacing={2} direction="column">
            <Typography
              component="h2"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            >
              {`Selected (${right.length})`}
            </Typography>
            {customList(right)}
            {selectButton(right, 'selected')}
          </Stack>
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 3,
        }}
      >
        <Stack spacing={2} direction="column" mt={1}>
          <Button
            onClick={() => onSave && onSave(right)}
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
          >
            OK
          </Button>
          <Button onClick={() => onCancel && onCancel()} variant="outlined">
            Cancel
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
